import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { IPlanningToolReportsItem } from '../../../../component/solution/Interfaces';
import Logo from "../../../../assets/img/logo.png";
import bkLogo from "../../../../assets/img/birkenstock_logo.png";
import hdLogo from "../../../../assets/img/hey_dude.png";
import tbLogo from "../../../../assets/img/ted_baker.png";
import dcLogo from "../../../../assets/img/digicape_logo_black.png";
import htLogo from "../../../../assets/img/hi-tec.svg"; 
import crLogo from "../../../../assets/img/crocs_logo_new.png"
import okLogo from "../../../../assets/img/oakley_logo.png"
import vaLogo from "../../../../assets/img/va-logo.png";
import uaLogo from "../../../../assets/img/ua-logo.png";
import sumpLogo from "../../../../assets/img/sump_logo.png";
import ShowErrorAlert from '../../../../component/ShowErrorAlert';
import { RotateCcw, Save } from 'lucide-react';




export const AresSettings:React.FC = () => {

     const {userDetail, showOpenSettings, setShowOpenSettings, fetchData, showErrorAlert, responseErrorMessage}  = useAuth();
     const planningToolReportsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/app/planning-tool/settings?prefix=Reports&recursive=true`;
     const planningToolReportsUpdateQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/app/planning-tool/settings`;


     const [show, setShow] = useState(false);
     const [activeSection, setActiveSection] = useState('reports'); // Default to 'general' section

     const [reportsState, setReportsState] = useState<IPlanningToolReportsItem[] | null>(null);
     const [compareReportsState, setCompareReportsState] = useState<IPlanningToolReportsItem[] | null>(null);
     
     const [settingUpdateComplete, setSettingUpdateComplete] = useState<boolean>(false);
     const [savingReportSettingId, setSavingReportSettingId] = useState<string | null>(null);
     const [shouldFadeOut, setShouldFadeOut] = useState(false);


    
     useEffect(() => {
       if(showOpenSettings){
            setShow(true);
            fetchPlanningToolReports();
        }
     }, [showOpenSettings]);


     useEffect(() => {
        if (settingUpdateComplete) {
            // Start fading after 4.5s
            const fadeTimer = setTimeout(() => {
                setShouldFadeOut(true);
            }, 4500);

            // Hide alert completely after 5s
            const hideTimer = setTimeout(() => {
                setSettingUpdateComplete(false);
                setShouldFadeOut(false); // reset for future
            }, 5000);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [settingUpdateComplete]);


    const fetchPlanningToolReports = () => {
        fetchData(planningToolReportsQueryUrl, { method: "GET" }, processPlanningToolReportsData);
    }

    const processPlanningToolReportsData = (result: any) => {

        if (!Array.isArray(result)) { return; }
    
        const reportItems: IPlanningToolReportsItem[] = result.map((entry: any) => ({...entry}));
        
        setReportsState(reportItems);
        setCompareReportsState(reportItems);
    };

    
    const getCustomerLogoDetails = (company: string | undefined): { src: string; width: number } => {
        switch (company) {
            case "ares-hd":
                return { src: hdLogo, width: 50 };
            case "ares-cr":
                return { src: crLogo, width: 120 };
            case "ares-ok":
            case "ares-va":
                return { src: vaLogo, width: 90 };
            case "ares-ua":
                return { src: uaLogo, width: 60 };
            case "ares-tb":
                return { src: tbLogo, width: 99 };
            case "stockanywhere-sump":
                return { src: sumpLogo, width: 80 };
            case "ares-bk":
                return { src: bkLogo, width: 120 };
            case "digicape":
            case "stockanywhere-digicape":
                return { src: dcLogo, width: 120 };
            case "hi-tec":
            case "stockanywhere-hi-tec":
                return { src: htLogo, width: 120 };
            default:
                return { src: Logo, width: 120 };
        }
    };

	const company = process.env.REACT_APP_COMPANY;
    const { src: customerLogo, width: logoWidth } = getCustomerLogoDetails(company);


    const handleMenuClick = (section: string) => {
        setActiveSection(section);
    };

    const handleClose = () => {
        setShow(false);
        setActiveSection('reports');
        setShowOpenSettings(false);
        setReportsState(null);
        setCompareReportsState(null)
    };


    const hasUnsavedChanges = (id: string) => {
        const current = reportsState?.find((r) => r.id === id);
        const original = compareReportsState?.find((r) => r.id === id);

        if (!current || !original) return false;

        // console.log('================================');
        // console.log(JSON.stringify(current.value) !== JSON.stringify(original.value));
        // console.log('================================');

        return JSON.stringify(current.value) !== JSON.stringify(original.value);
    };


    const handleReportChange = (id: string, newValue: IPlanningToolReportsItem['value']) => {
        setReportsState((prev) =>
            prev?.map((item) => {
            if (item.id !== id) return item;

            let coercedValue: any = newValue;

            // Coerce types as needed
            if (typeof item.value === 'number') {
                coercedValue = Number(newValue);
            } else if (typeof item.value === 'boolean') {
                coercedValue = Boolean(newValue);
            } else if (Array.isArray(item.value)) {
                coercedValue =
                typeof newValue === 'string'
                    ? newValue.split('\n').map((v) => v.trim()).filter(Boolean)
                    : newValue;
            }

            return { ...item, value: coercedValue };
            }) || []
        );
    };



    const resetUnsavedChanges = () => {
        if (!compareReportsState) return;
        setReportsState(compareReportsState.map(item => ({ ...item })));
    };

    const hasAnyUnsavedChanges = (): boolean => {
        if (!reportsState || !compareReportsState) return false;
        return reportsState.some((item) => hasUnsavedChanges(item.id));
    };

    // const handleSaveAllReports = () => {
    //     if (!reportsState || !compareReportsState) return;

    //     const changedItem = reportsState.find((item) => hasUnsavedChanges(item.id));
    //     if (!changedItem) return;

    //     setSavingReportSettingId(changedItem.id);

    //     fetchData(`${planningToolReportsUpdateQueryUrl}/${changedItem.id}`, {
    //         method: "POST",
    //         body: JSON.stringify(changedItem.value),
    //     }, (result: any, status: string) => {
    //         setSavingReportSettingId(null);

    //         if (status === "200") {
    //         setSettingUpdateComplete(true);

    //         // Update baseline state for the saved item
    //         setCompareReportsState((prev) =>
    //             prev?.map((item) =>
    //             item.id === changedItem.id ? { ...item, value: changedItem.value } : item
    //             ) || null
    //         );
    //         }
    //     });
    // };

    const handleSaveAllReports = () => {
        if (!reportsState || !compareReportsState) return;

        const changedItems = reportsState
            .filter((item) => hasUnsavedChanges(item.id))
            .map((item) => ({
                settingId: item.id,
                value: item.value
            }));

        if (changedItems.length === 0) return;

        // Optional: block UI during save
        setSavingReportSettingId('bulk'); // any string that indicates bulk save

        console.log(`=============== Mutiple Settings Changed ================`);
        console.log(changedItems);
        console.log(`=========================================================`);

        fetchData(planningToolReportsUpdateQueryUrl, {
            method: "POST",
            body: JSON.stringify(changedItems),
            headers: { 'Content-Type': 'application/json' }
        }, (result: any, status: string) => {
            setSavingReportSettingId(null);

            if (status === "200") {
                setSettingUpdateComplete(true);

                // Update compareReportsState to match new values
                setCompareReportsState((prev) =>
                    prev?.map((item) => {
                        const changedItem = changedItems.find(ci => ci.settingId === item.id);
                        return changedItem ? { ...item, value: changedItem.value } : item;
                    }) || null
                );
            }
        });
    };



    const atsSummaryItems = reportsState?.filter(item =>
        item.name.includes('ATS.Summary')
    );

    const atsPlanningItems = reportsState?.filter(item =>
        item.name.includes('ATS.Planning')
    );

    const otherItems = reportsState?.filter(item =>
        !item.name.includes('ATS.Summary') && !item.name.includes('ATS.Planning')
    );

    const renderReportItem = (item: IPlanningToolReportsItem) => (
        <div key={item.id} className="mb-4"  style={{ backgroundColor: "white", padding: "0.5rem", borderRadius: "0.5rem", border: "1px solid #e1e5ec" }}>
            <div
                style={{ backgroundColor: "#f9f9f9",  padding: "0.3rem", borderRadius: "0.5rem", border: "1px solid #e1e5ec"  }}
            >
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="modal-title text-secondary fw-bolder ps-1">
                    {item.name}
                </h6>
                {hasUnsavedChanges(item.id) && (
                  <span className="badge bg-danger text-white ms-2">Unsaved</span>
                )}
            </div>

            <div className="mb-3"  >
                {item.description && (
                <label className="form-label fw-semibold" style={{ paddingLeft: "0.75rem", color: "#aba8a7" }}>
                    {item.description}
                </label>
                )}

                {typeof item.value === 'string' || typeof item.value === 'number' ? (
                <input
                    className="form-control"
                    type={typeof item.value === 'number' ? 'number' : 'text'}
                    value={item.value}
                    onChange={(e) => handleReportChange(item.id, e.target.value)}
                />
                ) : typeof item.value === 'boolean' ? (
                <div className="form-check ms-2">
                    <input
                    className="form-check-input"
                    type="checkbox"
                    checked={item.value}
                    onChange={(e) => handleReportChange(item.id, e.target.checked)}
                    />
                    <label className="form-check-label ms-2">Enabled</label>
                </div>
                ) : Array.isArray(item.value) ? (
                   <textarea
                    className="form-control"
                    rows={item.value.length || 2}
                    value={item.value.join('\n')}
                    onChange={(e) =>
                        handleReportChange(
                        item.id,
                        e.target.value.split('\n') // no trim or filter here
                        )
                    }
                    />
                ) : null}
            </div>

            <div className="text-end">
                {/* {savingReportSettingId === item.id && (
                  <span className="spinner-border spinner-border-sm me-2" />
                )} */}
                {(savingReportSettingId === item.id || 
                    (savingReportSettingId === 'bulk' && hasUnsavedChanges(item.id))) && (
                    <span className="spinner-border spinner-border-sm me-2" />
                )}
            </div>
          </div>
        </div>
    );


    const IconButtonOrPlaceholder = ({ onClick, disabled, icon, title, className = '', variant = 'outline-secondary'}: { onClick: () => void; disabled: boolean; icon: React.ReactNode; title: string; className?: string; variant?: 'dark' | 'outline-secondary'; }) => {
        const baseStyle = { height: '34px', width: '34px', padding: '0', borderRadius: '6px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.85 : 1 };
        const variantClass = variant === 'dark' ? 'btn btn-md btn-dark' : 'btn btn-md btn-outline-secondary';

        if (disabled) {
            return (
            <div className={`${variantClass} d-flex align-items-center justify-content-center ${className}`} style={baseStyle} title={title}>
                {icon}
            </div>
            );
        }

        return (
            <button className={`${variantClass} d-flex align-items-center justify-content-center ${className}`} onClick={onClick} style={baseStyle} title={title}>
              {icon}
            </button>
        );
    };



     return(
        <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static"  dialogClassName="custom-full-modal">
            <Modal.Header closeButton>
                <Modal.Title>Settings Pannel</Modal.Title>
            </Modal.Header>

              <Modal.Body>
                <div className="d-flex">
                  {/* Left Menu */}
                   <div className="settings-menu p-3" style={{ width: '250px', borderRight: '1px solid #f1f1f1bd', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div className="d-flex align-items-center pb-4 mb-2" style={{ borderBottom: '1px solid #f1f1f1bd', width: "100%"}}>
                            <img src={customerLogo} alt="Company Logo" style={{ width: `${logoWidth}px`, maxHeight: '60px', objectFit: 'contain' }} className="me-3" />
                            <div className="text-start">
                                <div className="fw-semibold">{userDetail?.userName}</div>
                            </div>
                        </div>
                        <ul className="nav flex-column">
                            <li className="nav-item">
                            <Button
                                variant="link"
                                onClick={() => handleMenuClick('reports')}
                                className={`settings-menu-link ${activeSection === 'reports' ? 'active' : ''}`}
                            >
                                Reports
                            </Button>
                            </li>
                        </ul>
                    </div>
                

                    {/* Right Content Area */}
                    <div className="content p-3" style={{ flex: 1 }}>
                      { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 

                      { settingUpdateComplete && (
                            <div className={`alert d-flex align-items-center py-2 px-3 mb-2 ${shouldFadeOut ? 'fading-out' : ''}`} role="alert" 
                                    style={{ backgroundColor: "#c7f5bf", border: "1px solid rgb(222, 235, 237)", color: "#037012", transition: "opacity 0.5s ease-in-out", opacity: shouldFadeOut ? 0 : 1 }}>
                                    <span className="flex-grow-1 text-center">
                                    Your settings has beem successfully updated.
                                    </span>
                                    <button type="button" 
                                    style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', backgroundColor: '#c7f5bf', color: '#e53e3e', borderRadius: '8px', padding: '6px', height: '32px', width: '32px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                                    aria-label="Close"
                                    onClick={() => setSettingUpdateComplete(false)} 
                                    >
                                    <svg style={{ width: '12px', height: '12px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg"  fill="none" viewBox="0 0 14 14">
                                        <path stroke="#037012" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                                    </svg>
                                </button>
                            </div>
                        )}


                       {activeSection === 'reports' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="modal-title text-uppercase text-secondary fw-bolder m-0" style={{ fontSize: '14px' }}>
                                    Report Settings
                                </h5>

                                  <div className="d-flex gap-2">
                                    <IconButtonOrPlaceholder
                                        onClick={resetUnsavedChanges}
                                        disabled={!hasAnyUnsavedChanges() || savingReportSettingId !== null}
                                        icon={<RotateCcw size={18} />}
                                        title="Reset unsaved changes"
                                        variant={hasAnyUnsavedChanges() ? 'dark' : 'outline-secondary'}
                                    />

                                    <IconButtonOrPlaceholder
                                        onClick={handleSaveAllReports}
                                        disabled={!hasAnyUnsavedChanges() || savingReportSettingId !== null}
                                        icon={
                                            savingReportSettingId ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <Save size={18} />
                                            )
                                        }
                                        title="Save unsaved changes"
                                        variant={hasAnyUnsavedChanges() ? 'dark' : 'outline-secondary'} 
                                    />
                                </div>
                           </div>

                            <p className="text-muted" style={{ fontSize: '13px' }}>
                               Here you can adjust report settings for the application.
                            </p>

                              

                              {atsSummaryItems && atsSummaryItems.length > 0 && (
                                <div style={{
                                    backgroundColor: "#f9f9f9",
                                    padding: "1rem",
                                    borderRadius: "0.5rem",
                                    border: "1px solid #e1e5ec"
                                }}>
                                    <div className="d-flex align-items-center justify-content-between border-bottom mb-2">
                                    <h6 className="modal-title text-uppercase text-secondary fw-bolder ps-1">
                                        ATS Summary
                                    </h6>
                                    </div>
                                    {atsSummaryItems.map(item => renderReportItem(item))}
                                </div>
                                )}

                                {atsPlanningItems && atsPlanningItems.length > 0 && (
                                <>
                                    <hr className="mb-4 mt-4" style={{ borderTop: '4px solid red' }} />
                                    <div  style={{
                                    backgroundColor: "#f9f9f9",
                                    padding: "1rem",
                                    borderRadius: "0.5rem",
                                    border: "1px solid #e1e5ec"
                                    }}>
                                    <div className="d-flex align-items-center justify-content-between border-bottom mb-2">
                                        <h6 className="modal-title text-uppercase text-secondary fw-bolder ps-1">
                                        ATS Planning
                                        </h6>
                                    </div>
                                    {atsPlanningItems.map(item => renderReportItem(item))}
                                    </div>
                                </>
                                )}

                                {otherItems && otherItems.length > 0 && (
                                <>
                                    <hr  className="mb-4 mt-4" style={{ borderTop: '4px solid red' }} />
                                    <div  style={{
                                    backgroundColor: "#f9f9f9",
                                    padding: "1rem",
                                    borderRadius: "0.5rem",
                                    border: "1px solid #e1e5ec"
                                    }}>
                                    <div className="d-flex align-items-center justify-content-between border-bottom mb-2">
                                        <h6 className="modal-title text-uppercase text-secondary fw-bolder ps-1">
                                        Other Settings
                                        </h6>
                                    </div>
                                    {otherItems.map(item => renderReportItem(item))}
                                    </div>
                                </>
                                )}

                      
                        </div>
                        )}

                    </div>
                </div>
            </Modal.Body>
       </Modal>
    );
}


/*


    // const handleSaveReport = (id: string) => {
    //     const itemToSave = reportsState?.find((r) => r.id === id);
    //     if (!itemToSave) return;

    //     //console.log("Saving Report Item:", itemToSave);

    //     setSavingReportSettingId(id); // start spinner + disable all buttons

    //     //Example:
    //     fetchData(`${planningToolReportsUpdateQueryUrl}/${id}`, 
    //         { method: "POST", body: JSON.stringify(itemToSave.value) }, 
    //         (result: any, status: string) => {
    //             setSavingReportSettingId(null); // done saving

    //              if (status === "200") {
    //                 setSettingUpdateComplete(true);

    //                 // Fetch latest data
    //                 fetchData(planningToolReportsQueryUrl, { method: "GET" }, (result: any) => {
    //                 if (!Array.isArray(result)) return;

    //                 const updated = result.map((entry: any) => ({ ...entry }));

    //                 // Keep any unsaved edits from previous state
    //                 const merged = updated.map((item) => {
    //                     const unsaved = reportsState?.find((r) => r.id === item.id);
    //                     const original = compareReportsState?.find((r) => r.id === item.id);
    //                     const isChanged = unsaved && original && JSON.stringify(unsaved.value) !== JSON.stringify(original.value);

    //                     return isChanged && item.id !== id ? unsaved : item;
    //                 });

    //                   setReportsState(merged);
    //                   setCompareReportsState(updated); // baseline is always backend
    //                 });
    //             }

    //     });
    // };


*/

